// src/pages/Profile.js
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({ name: user?.name || '', phone: user?.phone || '', address: user?.address || '', gstin: user?.gstin || '' });
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('info');

  const saveProfile = async () => {
    setLoading(true);
    try {
      await api.put('/auth/profile', form);
      await refreshUser();
      toast.success('Profile updated');
    } catch (err) { toast.error(err.response?.data?.message || 'Update failed'); }
    finally { setLoading(false); }
  };

  const changePassword = async () => {
    if (pwForm.new_password !== pwForm.confirm) return toast.error('Passwords do not match');
    setLoading(true);
    try {
      await api.put('/auth/change-password', { current_password: pwForm.current_password, new_password: pwForm.new_password });
      toast.success('Password changed');
      setPwForm({ current_password: '', new_password: '', confirm: '' });
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  const statusColor = { verified: 'var(--neon-green)', pending: 'var(--neon-yellow)', under_review: 'var(--neon-cyan)', rejected: 'var(--neon-pink)' }[user?.kyc_status] || 'var(--muted)';

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '3rem 2.5rem' }}>
      <div className="section-tag">Account</div>
      <h1 className="section-title">MY <span style={{ color: 'var(--neon-cyan)' }}>PROFILE</span></h1>

      {/* PROFILE HEADER */}
      <div className="card" style={{ marginTop: '1.5rem', display: 'flex', gap: '2rem', alignItems: 'center', flexWrap: 'wrap', borderTop: '2px solid var(--neon-cyan)' }}>
        <div style={{ width: '80px', height: '80px', background: 'linear-gradient(135deg,#00ff88,#00e5ff)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '2rem', color: '#000', letterSpacing: '2px', flexShrink: 0 }}>
          {user?.name?.charAt(0)}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '1.5rem', letterSpacing: '3px' }}>{user?.name}</div>
          <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.7rem', color: 'var(--muted)', marginTop: '0.2rem' }}>{user?.email}</div>
          <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.65rem', marginTop: '0.4rem' }}>
            <span style={{ color: statusColor }}>KYC: {user?.kyc_status?.toUpperCase().replace('_', ' ')} &nbsp;|&nbsp;</span>
            <span style={{ color: 'var(--muted)' }}>GCI: {user?.gci_id || 'Pending'} &nbsp;|&nbsp;</span>
            <span style={{ color: 'var(--muted2)' }}>{user?.entity_type} — {user?.sector}</span>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.6rem', color: 'var(--muted)', letterSpacing: '2px' }}>INR BALANCE</div>
          <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '1.5rem', color: 'var(--neon-green)', letterSpacing: '2px' }}>₹{(user?.balance || 0).toLocaleString()}</div>
          <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.6rem', color: 'var(--muted)', letterSpacing: '2px', marginTop: '0.4rem' }}>CARBON BALANCE</div>
          <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '1.2rem', color: 'var(--neon-cyan)', letterSpacing: '2px' }}>{user?.carbon_balance || 0} tCO₂e</div>
        </div>
      </div>

      {/* TABS */}
      <div style={{ display: 'flex', gap: 0, margin: '2rem 0 1.5rem', borderBottom: '1px solid var(--border)' }}>
        {[['info', 'Personal Info'], ['security', 'Security'], ['verification', 'Verification']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{ padding: '0.8rem 1.5rem', background: 'transparent', border: 'none', borderBottom: `2px solid ${tab === k ? 'var(--neon-green)' : 'transparent'}`, color: tab === k ? 'var(--neon-green)' : 'var(--muted)', fontFamily: 'Space Mono, monospace', fontSize: '0.7rem', letterSpacing: '2px', textTransform: 'uppercase', cursor: 'pointer' }}>
            {l}
          </button>
        ))}
      </div>

      {tab === 'info' && (
        <div className="card">
          <div className="form-group"><label className="form-label">Full Name</label>
            <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
          <div className="form-group"><label className="form-label">Phone</label>
            <input className="form-input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
          <div className="form-group"><label className="form-label">GSTIN (Optional)</label>
            <input className="form-input" value={form.gstin} onChange={e => setForm({ ...form, gstin: e.target.value })} /></div>
          <div className="form-group"><label className="form-label">Address</label>
            <textarea className="form-textarea" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} rows={3} /></div>
          <div style={{ display: 'flex', gap: '0.5rem', fontFamily: 'Space Mono, monospace', fontSize: '0.65rem', color: 'var(--muted)', marginBottom: '1rem' }}>
            <span>PAN: {user?.pan}</span> <span>|</span> <span>Email: {user?.email} (cannot change)</span>
          </div>
          <button className="btn btn-primary" onClick={saveProfile} disabled={loading}>{loading ? 'SAVING...' : 'SAVE CHANGES'}</button>
        </div>
      )}

      {tab === 'security' && (
        <div className="card">
          <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '1.3rem', letterSpacing: '2px', marginBottom: '1.5rem' }}>CHANGE PASSWORD</div>
          <div className="form-group"><label className="form-label">Current Password</label>
            <input className="form-input" type="password" value={pwForm.current_password} onChange={e => setPwForm({ ...pwForm, current_password: e.target.value })} /></div>
          <div className="form-group"><label className="form-label">New Password</label>
            <input className="form-input" type="password" value={pwForm.new_password} onChange={e => setPwForm({ ...pwForm, new_password: e.target.value })} placeholder="Min 8 chars, uppercase, number" /></div>
          <div className="form-group"><label className="form-label">Confirm New Password</label>
            <input className="form-input" type="password" value={pwForm.confirm} onChange={e => setPwForm({ ...pwForm, confirm: e.target.value })} /></div>
          <button className="btn btn-secondary" onClick={changePassword} disabled={loading}>{loading ? 'CHANGING...' : 'CHANGE PASSWORD'}</button>
        </div>
      )}

      {tab === 'verification' && (
        <div className="card">
          <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '1.3rem', letterSpacing: '2px', marginBottom: '1.5rem' }}>VERIFICATION STATUS</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            {[
              { l: 'KYC Status', v: user?.kyc_status?.toUpperCase().replace('_', ' '), c: statusColor },
              { l: 'GCI Registry ID', v: user?.gci_id || 'Not Assigned', c: 'var(--neon-green)' },
              { l: 'Verified On', v: user?.verified_at ? new Date(user.verified_at).toLocaleDateString('en-IN') : 'Pending', c: 'var(--text)' },
              { l: 'Account Since', v: new Date(user?.created_at).toLocaleDateString('en-IN'), c: 'var(--text)' },
            ].map(({ l, v, c }) => (
              <div key={l} className="card" style={{ background: 'var(--surface2)' }}>
                <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.6rem', color: 'var(--muted)', letterSpacing: '2px', marginBottom: '0.4rem' }}>{l}</div>
                <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.85rem', color: c }}>{v}</div>
              </div>
            ))}
          </div>
          {user?.kyc_status !== 'verified' && (
            <div style={{ marginTop: '1.5rem' }}>
              <a href="/kyc" className="btn btn-orange">Complete KYC Verification →</a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
